import { useState } from "react";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useNavigate } from "react-router-dom";
import {
  filterLetters,
  filterPhone,
  lettersKeyDown,
  phoneKeyDown,
} from "./utils/inputFilters";
import { API_BASE } from "./utils/apiBase";

const ENABLE_PUBLIC_APPLY = false;

export default function MainDashboard() {
  const navigate = useNavigate();
  const [openApply, setOpenApply] = useState(false);

  const [formData, setFormData] = useState({
    firstname: "",
    middlename: "",
    noMiddleName: false,
    lastname: "",
    birthdate: "",
    gender: "Male",
    civil_status: "Single",
    address: "",
    email: "",
    contact: "",
    role_applied: '',
    resumeFile: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [ageError, setAgeError] = useState("");
  const [nameErrors, setNameErrors] = useState({
    firstname: "",
    middlename: "",
    lastname: "",
  });
  // track touched fields so we only show field-level errors after interaction
  const [touched, setTouched] = useState({});
  // track whether the user has attempted to submit the form; summary only appears after first attempt
  const [triedSubmit, setTriedSubmit] = useState(false);
  const NAME_REGEX = /^[A-Za-z][A-Za-z\s\-']{1,48}$/;

  const handleCloseApply = () => setOpenApply(false);
  // Clear prior validation/errors/touched state when opening the dialog so the
  // validation summary and field errors do not appear immediately on open.
  const openApplyClean = () => {
    setFormErrors({});
    setAgeError("");
    setNameErrors({ firstname: "", middlename: "", lastname: "" });
    setTouched({});
    setTriedSubmit(false);
    // reset form data to initial empty values to ensure a clean form on open
    setFormData({
      firstname: "",
      middlename: "",
      noMiddleName: false,
      lastname: "",
      birthdate: "",
      gender: "Male",
      civil_status: "Single",
      address: "",
      email: "",
      contact: "",
      role_applied: '',
      resumeFile: null,
    });
    setOpenApply(true);
  };

  const validateNameField = (field, value, required = true) => {
    let error = "";
    const trimmed = String(value || "").trim();
    if (!trimmed && required) {
      error = "This field is required.";
    } else if (trimmed && !NAME_REGEX.test(trimmed)) {
      error = "Use real name only (letters, spaces, -, ' only; at least 2 letters).";
    }
    setNameErrors((prev) => ({ ...prev, [field]: error }));
    setFormErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const computeAgeFromBirthdate = (birthdate) => {
    if (!birthdate) return null;
    const b = new Date(birthdate);
    if (isNaN(b.getTime())) return null;
    const today = new Date();
    let age = today.getFullYear() - b.getFullYear();
    const m = today.getMonth() - b.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < b.getDate())) age--;
    return age;
  };

  const validateAge = (birthdate) => {
    if (!birthdate) {
      setAgeError("Birthdate is required");
      setFormErrors((prev) => ({ ...prev, birthdate: "Birthdate is required" }));
      return false;
    }
    const age = computeAgeFromBirthdate(birthdate);
    if (!Number.isFinite(age)) {
      setAgeError("Invalid birthdate");
      setFormErrors((prev) => ({ ...prev, birthdate: "Invalid birthdate" }));
      return false;
    }
    if (age < 18) {
      setAgeError("Applicant must be at least 18 years old.");
      setFormErrors((prev) => ({ ...prev, birthdate: "Applicant must be at least 18 years old." }));
      return false;
    }
    setAgeError("");
    setFormErrors((prev) => ({ ...prev, birthdate: "" }));
    return true;
  };

  const handleBirthdateChange = (value) => {
    setTouched((prev) => ({ ...prev, birthdate: true }));
    setFormData((prev) => ({ ...prev, birthdate: value, age: computeAgeFromBirthdate(value) }));
    validateAge(value);
  };

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    if (name === 'noMiddleName') {
      const checked = Boolean(e.target.checked);
      setTouched(prev => ({ ...prev, noMiddleName: true, middlename: true }));
      setNameErrors(prev => ({ ...prev, middlename: '' }));
      setFormErrors(prev => ({ ...prev, middlename: '' }));
      setFormData((prev) => ({
        ...prev,
        noMiddleName: checked,
        middlename: checked ? '' : prev.middlename,
      }));
      return;
    }
    if (name === 'firstname' || name === 'middlename' || name === 'lastname') {
      value = filterLetters(value);
    } else if (name === 'contact') {
      value = filterPhone(value);
    }
    // If birthdate changed, compute age and track validation
    if (name === 'birthdate') {
      // handled by DatePicker
      setFormData((prev) => ({ ...prev, [name]: value }));
      return;
    }
    // mark field touched on change
    setTouched(prev => ({ ...prev, [name]: true }));
    if (name === 'firstname' || name === 'middlename' || name === 'lastname') {
      const required = name !== 'middlename';
      if (triedSubmit || nameErrors[name]) validateNameField(name, value, required);
    }
    // inline validation for email
    if (name === 'email') {
      if (!value || String(value).trim() === '') {
        setFormErrors(prev => ({ ...prev, email: 'Email is required' }));
      } else {
        const re = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
        setFormErrors(prev => ({ ...prev, email: re.test(value) ? '' : 'Email must contain @ and a valid domain (e.g. name@example.com)' }));
      }
    }
    // inline validation for contact (09XXXXXXXXX)
    if (name === 'contact') {
      if (!value || String(value).trim() === '') {
        setFormErrors(prev => ({ ...prev, contact: 'Contact is required' }));
      } else {
        const digits = String(value).replace(/\D/g, '');
        setFormErrors(prev => ({ ...prev, contact: /^09\d{9}$/.test(digits) ? '' : 'Contact must start with 09 and be 11 digits (e.g. 09171234567)' }));
      }
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const maxBytes = 2 * 1024 * 1024; // 2MB
    // Basic type check by MIME and fallback to extension check for older browsers
    const extOk = /\.(pdf|docx?|PDF|DOCX?)$/.test(file.name);
    if (!allowedTypes.includes(file.type) && !extOk) {
      setFormErrors(prev => ({ ...prev, resumeFile: 'Unsupported file type. Allowed: PDF, DOC, DOCX' }));
      setSnackbar({ open: true, severity: 'error', message: 'Unsupported resume file type. Use PDF/DOC/DOCX.' });
      setFormData((prev) => ({ ...prev, resumeFile: null }));
      return;
    }
    if (file.size > maxBytes) {
      setFormErrors(prev => ({ ...prev, resumeFile: 'File too large. Maximum 2MB allowed.' }));
      setSnackbar({ open: true, severity: 'error', message: 'Resume file too large. Max 2MB.' });
      setFormData((prev) => ({ ...prev, resumeFile: null }));
      return;
    }
    setFormErrors(prev => ({ ...prev, resumeFile: '' }));
    setFormData((prev) => ({ ...prev, resumeFile: file }));
  };

  const handleSubmit = async () => {
    // client-side validation - build field-specific errors
    // indicate that the user attempted to submit so validation summary can appear
    setTriedSubmit(true);

    const errs = {};
    if (!formData.contact || String(formData.contact).trim() === '') errs.contact = 'Contact is required';
    if (!formData.email || String(formData.email).trim() === '') errs.email = 'Email is required';
    if (!formData.role_applied) errs.role_applied = 'Please select a role';

    // merge with existing inline validation errors (format checks)
    if (formErrors.email) errs.email = formErrors.email;
    if (formErrors.contact) errs.contact = formErrors.contact;
    if (String(formData.email || '').indexOf('@') === -1) errs.email = 'Please provide a valid email';
    const digits = String(formData.contact || '').replace(/\D/g, '');
    if (!/^09\d{9}$/.test(digits)) errs.contact = 'Contact must start with 09 and be 11 digits';

    const validFirst = validateNameField('firstname', formData.firstname, true);
    const validMiddle = formData.noMiddleName ? true : validateNameField('middlename', formData.middlename, false);
    const validLast = validateNameField('lastname', formData.lastname, true);
    const isAgeValid = validateAge(formData.birthdate);

    if (!validFirst || !validMiddle || !validLast || !isAgeValid) {
      if (!formData.birthdate || String(formData.birthdate).trim() === '') {
        errs.birthdate = 'Birthdate is required';
      } else if (!Number.isFinite(computeAgeFromBirthdate(formData.birthdate))) {
        errs.birthdate = 'Invalid birthdate';
      } else if (!isAgeValid) {
        errs.birthdate = 'Applicant must be at least 18 years old.';
      }
    }

    if (Object.keys(errs).length > 0) {
      setFormErrors(prev => ({ ...prev, ...errs }));
      // focus first invalid field by name
      const firstInvalid = Object.keys(errs)[0];
      setTimeout(() => {
        try { const el = document.querySelector(`[name="${firstInvalid}"]`); if (el && typeof el.focus === 'function') el.focus(); } catch (e) {}
      }, 50);
      return;
    }

    try {
      let response;
      if (formData.resumeFile) {
        const formPayload = new FormData();
        formPayload.append('firstname', formData.firstname);
        formPayload.append('middlename', formData.noMiddleName ? '' : (formData.middlename || ''));
        formPayload.append('lastname', formData.lastname);
        formPayload.append('birthdate', formData.birthdate);
        formPayload.append('gender', formData.gender);
        formPayload.append('civil_status', formData.civil_status);
        formPayload.append('address', formData.address || '');
        formPayload.append('email', formData.email);
        formPayload.append('contact', formData.contact);
        formPayload.append('role_applied', formData.role_applied);
        formPayload.append('resumeFile', formData.resumeFile);
        response = await fetch(API_BASE + '/api/applicants', {
          method: 'POST',
          body: formPayload
        });
      } else {
        const payload = {
          firstname: formData.firstname,
          middlename: formData.noMiddleName ? null : (formData.middlename || null),
          lastname: formData.lastname,
          birthdate: formData.birthdate,
          gender: formData.gender,
          civil_status: formData.civil_status,
          address: formData.address,
          email: formData.email,
          contact: formData.contact,
          role_applied: formData.role_applied
        };

        response = await fetch(API_BASE + '/api/applicants', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      if (!response.ok) {
        // Try to parse structured field errors from server
        let msg = 'Error submitting application';
        try {
          const data = await response.json();
          if (data && data.errors && typeof data.errors === 'object') {
            setFormErrors(prev => ({ ...prev, ...data.errors }));
            if (data.errors.birthdate) setAgeError(data.errors.birthdate);
            if (data.errors.firstname || data.errors.middlename || data.errors.lastname) {
              setNameErrors((prev) => ({
                ...prev,
                firstname: data.errors.firstname || prev.firstname,
                middlename: data.errors.middlename || prev.middlename,
                lastname: data.errors.lastname || prev.lastname,
              }));
            }
            // focus the first field with an error
            const first = Object.keys(data.errors)[0];
            setTimeout(() => {
              try { const el = document.querySelector(`[name="${first}"]`); if (el && typeof el.focus === 'function') el.focus(); } catch (e) {}
            }, 50);
            msg = Object.values(data.errors).join(', ');
          } else if (data && data.error) {
            msg = data.error;
          }
        } catch (e) {
          // Fallback to text when JSON isn't available
          try { const text = await response.text(); msg = text || msg; } catch (_) {}
        }
        setSnackbar({ open: true, severity: 'error', message: msg });
        return;
      }

      setSnackbar({ open: true, severity: 'success', message: 'Application submitted successfully!' });
      setOpenApply(false);
      setFormData({ firstname: '', middlename: '', noMiddleName: false, lastname: '', birthdate: '', gender: 'Male', civil_status: 'Single', address: '', email: '', contact: '', role_applied: '', resumeFile: null });
      setFormErrors({});
      setNameErrors({ firstname: "", middlename: "", lastname: "" });
      setAgeError("");
      setTriedSubmit(false);
    } catch (error) {
      setSnackbar({ open: true, severity: 'error', message: 'Error submitting application: ' + error.message });
    }
  };

  // formErrors may include birthdate
  // compute and show age next to birthdate

  const [snackbar, setSnackbar] = useState({ open: false, severity: 'info', message: '' });
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar((s) => ({ ...s, open: false }));
  };

  const currentDate = new Date().toLocaleDateString();

  // Focus helper used by clickable validation summary items
  const handleFocus = (message) => {
    // small helper to visually highlight an element briefly
    const highlightElement = (el) => {
      try {
        if (!el) return;
        const prevTransition = el.style.transition;
        const prevBox = el.style.boxShadow;
        el.style.transition = 'box-shadow 0.25s ease';
        el.style.boxShadow = '0 0 0 6px rgba(59,130,246,0.22)';
        setTimeout(() => {
          try { el.style.boxShadow = prevBox || ''; el.style.transition = prevTransition || ''; } catch (e) {}
        }, 1400);
      } catch (e) {
        // ignore
      }
    };
    try {
      let name = null;
      const m = String(message || '');
      if (m.includes('First name')) name = 'firstname';
      else if (m.includes('Last name')) name = 'lastname';
      else if (m.includes('Contact')) name = 'contact';
      else if (m.includes('Email')) name = 'email';
      else if (m.includes('Birthdate') || m.includes('Applicant must')) name = 'birthdate';
      else if (m.includes('role')) name = 'role_applied';
      // fallback: try to find any input that mentions the message in label
      let el = null;
      if (name) el = document.querySelector(`[name="${name}"]`) || document.querySelector(`input[name='${name}']`) || document.querySelector(`select[name='${name}']`);
      // fallback: try file input
      if (!el) el = document.querySelector('input[type=file]');

      if (el) {
        // scroll into view and focus
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) {}
        try { el.focus(); } catch (e) {}
        highlightElement(el);

        // small delay then try to open select/popover if applicable
        setTimeout(() => {
          try {
            const tag = (el.tagName || '').toLowerCase();
            const role = el.getAttribute && el.getAttribute('role');
            const hasPopup = el.getAttribute && el.getAttribute('aria-haspopup');
            // If it's a native select, click it
            if (tag === 'select') { el.click(); return; }
            // If element itself looks like a button/trigger, click it
            if (role === 'button' || hasPopup === 'listbox' || hasPopup === 'true') { el.click(); return; }
            // Try to find a nearby button with role=button or aria-haspopup
            let btn = el.closest('label')?.nextElementSibling || el.parentElement?.querySelector('[role="button"]') || el.parentElement?.querySelector('[aria-haspopup]');
            if (!btn) {
              // search upward for a trigger element in ancestors/siblings
              btn = document.querySelector(`[aria-controls][aria-haspopup]`) || document.querySelector('[role="button"][aria-haspopup]');
            }
            if (btn && typeof btn.click === 'function') btn.click();
          } catch (e) {
            // ignore
          }
        }, 80);
        return;
      }

      // If we couldn't find by name, try to find a label that contains a word from message
      const keywords = ['First name','Last name','Contact','Email','Birthdate','Role Applying For','Role'];
      for (const k of keywords) {
        if (m.includes(k)) {
          // find label with that text
          const labels = Array.from(document.querySelectorAll('label'));
          const found = labels.find(L => L.textContent && L.textContent.includes(k));
          if (found) {
            const forId = found.getAttribute('for');
            let target = null;
            if (forId) target = document.getElementById(forId);
            if (!target) target = found.nextElementSibling || found.parentElement?.querySelector('input,select,div[role="button"]');
            if (target) { try { target.scrollIntoView({ behavior: 'smooth', block: 'center' }); target.focus(); highlightElement(target); if (typeof target.click === 'function') target.click(); } catch (e) {} return; }
          }
        }
      }
    } catch (e) {
      // ignore focus failures
    }
  };

  return (
    <Box
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        background:
          "linear-gradient(160deg, #e7ebf0 0%, #f3f4f6 60%, #e8ebf1 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* angled white rectangle */}
      <Box
        sx={{
          position: "absolute",
          top: "-30%",
          left: "-10%",
          width: "160%",
          height: "200%",
          background:
            "linear-gradient(120deg, rgba(255,255,255,0.9) 0%, rgba(245,246,248,0.5) 60%, rgba(255,255,255,0) 100%)",
          transform: "rotate(-25deg)",
          zIndex: 0,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: { xs: 1.5, sm: 2 },
          px: { xs: 2, sm: 4 },
          py: { xs: 1.5, sm: 2 },
          zIndex: 2,
        }}
      >
        <Typography
          variant="h6"
          fontWeight={600}
          sx={{ fontSize: { xs: "1rem", sm: "1.25rem" }, maxWidth: { xs: 180, sm: "none" } }}
        >
          EZRA GENERAL SERVICES
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            width: { xs: "100%", sm: "auto" },
            justifyContent: { xs: "flex-start", sm: "flex-end" },
          }}
        >
          <Button
            variant="contained"
            onClick={() => navigate("/contact")}
            sx={{
              mr: 0,
              px: { xs: 2.25, sm: 3 },
              py: { xs: 1, sm: 1.2 },
              fontSize: { xs: "0.95rem", sm: "1rem" },
              minWidth: { xs: 120, sm: "auto" },
              borderRadius: "999px",
              background:
                "linear-gradient(90deg, rgba(67,56,202,0.9), rgba(59,130,246,0.9))",
              color: "#fff",
              boxShadow: "0 6px 18px rgba(67,56,202,0.25)",
              backdropFilter: "blur(4px)",
              transition: "all 0.25s ease",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 10px 24px rgba(67,56,202,0.35)",
                background:
                  "linear-gradient(90deg, rgba(59,130,246,1), rgba(67,56,202,1))",
              },
            }}
          >
            Contact Us
          </Button>

          <Button
            variant="contained"
            onClick={() => navigate("/login")}
            sx={{
              px: { xs: 2.25, sm: 3 },
              py: { xs: 1, sm: 1.2 },
              fontSize: { xs: "0.95rem", sm: "1rem" },
              minWidth: { xs: 120, sm: "auto" },
              borderRadius: "999px",
              background:
                "linear-gradient(90deg, rgba(59,130,246,0.9), rgba(67,56,202,0.9))",
              color: "#fff",
              boxShadow: "0 6px 18px rgba(67,56,202,0.25)",
              backdropFilter: "blur(4px)",
              transition: "all 0.25s ease",
              "&:hover": {
                transform: "translateY(-3px)",
                boxShadow: "0 10px 24px rgba(59,130,246,0.35)",
                background:
                  "linear-gradient(90deg, rgba(67,56,202,1), rgba(59,130,246,1))",
              },
            }}
          >
            Login
          </Button>
        </Box>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          justifyContent: "center",
          px: { xs: 3, sm: 8 },
          py: { xs: 4, sm: 0 },
          position: "relative",
          zIndex: 2,
          overflow: "hidden",
        }}
      >
        {/* Cube box */}
        <Box
          sx={{
            position: "absolute",
            bottom: { xs: 36, sm: 80 },
            right: { xs: -24, sm: 160 },
            width: { xs: 132, sm: 180 },
            height: { xs: 132, sm: 180 },
            backgroundColor: "#ffffff",
            boxShadow: "30px 30px 70px rgba(0,0,0,0.08)",
            transform: "rotateX(15deg) rotateY(25deg)",
            borderRadius: "6px",
            opacity: { xs: 0.72, sm: 1 },
          }}
        />

        {/* Tagline */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            color: "#9ca3af",
            mb: 1,
            letterSpacing: 1.2,
            fontSize: { xs: "0.9rem", sm: "1.25rem" },
            maxWidth: { xs: 220, sm: "none" },
          }}
        >
          EZRA GENERAL SERVICES
        </Typography>

        <Typography
          variant="h2"
          fontWeight={800}
          sx={{
            lineHeight: 1.1,
            color: "#111827",
            fontSize: { xs: "2.05rem", sm: "2.7rem", md: "3.8rem" },
            maxWidth: { xs: 260, sm: 560, md: 760 },
          }}
        >
          Where <span style={{ color: "#3b82f6" }}>Clean</span> Meets{" "}
          <span style={{ color: "#4338ca" }}>Comfort</span>
        </Typography>

        {ENABLE_PUBLIC_APPLY && (
          <Button
            variant="contained"
            sx={{
              mt: 4,
              px: { xs: 6, md: 7 },
              py: { xs: 1.6, md: 1.8 },
              borderRadius: 999,
              fontSize: { xs: "1.05rem", md: "1.15rem" },
              backgroundColor: "#4338ca",
              textTransform: "none",
              boxShadow: "0 12px 36px rgba(67,56,202,0.25)",
              transition: "all 0.25s ease",
              "&:hover": {
                backgroundColor: "#312e81",
                transform: "translateY(-4px)",
                boxShadow: "0 16px 48px rgba(67,56,202,0.35)",
              },
            }}
            onClick={openApplyClean}
          >
            Apply
          </Button>
        )}
      </Box>

      {/* Apply Modal */}
      <Dialog open={ENABLE_PUBLIC_APPLY && openApply} onClose={handleCloseApply} maxWidth="sm" fullWidth>
        <DialogTitle>Job Application</DialogTitle>
        <DialogContent>
          <Box
            component="form"
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              mt: 1,
            }}
            noValidate
            autoComplete="off"
          >
            <TextField
              label="First Name"
              name="firstname"
              value={formData.firstname}
              onChange={handleChange}
              onBlur={(e) => validateNameField('firstname', e.target.value, true)}
              onKeyDown={lettersKeyDown}
              required
              fullWidth
              error={!!nameErrors.firstname && (touched.firstname || triedSubmit)}
              helperText={(touched.firstname || triedSubmit) ? (nameErrors.firstname || ' ') : ' '}
            />
            <TextField
              label="Middle Name"
              name="middlename"
              value={formData.middlename}
              onChange={handleChange}
              onBlur={(e) => validateNameField('middlename', e.target.value, false)}
              onKeyDown={lettersKeyDown}
              fullWidth
              disabled={formData.noMiddleName}
              error={!!nameErrors.middlename && (touched.middlename || triedSubmit)}
              helperText={(touched.middlename || triedSubmit) ? (nameErrors.middlename || ' ') : ' '}
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="noMiddleName"
                  checked={!!formData.noMiddleName}
                  onChange={handleChange}
                />
              }
              label="No Middle Name"
            />
            <TextField
              label="Last Name"
              name="lastname"
              value={formData.lastname}
              onChange={handleChange}
              onBlur={(e) => validateNameField('lastname', e.target.value, true)}
              onKeyDown={lettersKeyDown}
              required
              fullWidth
              error={!!nameErrors.lastname && (touched.lastname || triedSubmit)}
              helperText={(touched.lastname || triedSubmit) ? (nameErrors.lastname || ' ') : ' '}
            />
            <TextField
              label="Phone Number"
              name="contact"
              value={formData.contact}
              onChange={handleChange}
              onKeyDown={(e) => phoneKeyDown(e, formData.contact)}
              required
              fullWidth
              error={!!formErrors.contact && (touched.contact || triedSubmit)}
              helperText={(touched.contact || triedSubmit) ? (formErrors.contact || '') : ''}
            />
            <TextField
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              fullWidth
              error={!!formErrors.email && (touched.email || triedSubmit)}
              helperText={(touched.email || triedSubmit) ? (formErrors.email || '') : ''}
            />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DatePicker
                  label="Birthdate"
                  // DatePicker expects a Date object (or null). Construct from stored yyyy-mm-dd to avoid timezone shifts.
                  value={formData.birthdate ? (() => {
                    try {
                      const parts = String(formData.birthdate).split('-');
                      if (parts.length === 3) return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
                    } catch (e) {}
                    return null;
                  })() : null}
                  onChange={(newVal) => {
                    if (newVal) {
                      const bd = new Date(newVal);
                      const iso = `${bd.getFullYear()}-${String(bd.getMonth() + 1).padStart(2, '0')}-${String(bd.getDate()).padStart(2, '0')}`;
                      handleBirthdateChange(iso);
                    } else {
                      handleBirthdateChange('');
                    }
                  }}
                  slotProps={{
                    textField: {
                      name: 'birthdate',
                      fullWidth: true,
                      required: true,
                      helperText: (touched.birthdate || triedSubmit) ? (ageError || (formData.age ? `Age: ${formData.age}` : ' ')) : (formData.age ? `Age: ${formData.age}` : ' '),
                      error: !!ageError && (touched.birthdate || triedSubmit),
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: ageError ? 'error.main' : undefined,
                          },
                          '&:hover fieldset': {
                            borderColor: ageError ? 'error.dark' : undefined,
                          }
                        }
                      }
                    }
                  }}
                />
                <Tooltip title="Minimum age to apply: 18">
                  <InfoOutlinedIcon color="action" />
                </Tooltip>
              </Box>
            </LocalizationProvider>
            <FormControl fullWidth>
              <InputLabel id="civil-status-label">Civil Status</InputLabel>
              <Select
                labelId="civil-status-label"
                label="Civil Status"
                name="civil_status"
                value={formData.civil_status}
                onChange={handleChange}
              >
                <MenuItem value={'Single'}>Single</MenuItem>
                <MenuItem value={'Married'}>Married</MenuItem>
                <MenuItem value={'Widowed'}>Widowed</MenuItem>
                <MenuItem value={'Separated'}>Separated</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" required error={!!formErrors.role_applied}>
              <InputLabel id="role-applied-label">Role Applying For</InputLabel>
              <Select
                labelId="role-applied-label"
                label="Role Applying For"
                name="role_applied"
                value={formData.role_applied}
                onChange={(e) => { setTouched(prev => ({ ...prev, role_applied: true })); setFormData(f => ({ ...f, role_applied: e.target.value })); setFormErrors(prev => ({ ...prev, role_applied: '' })); }}
              >
                <MenuItem value={"Custodian"}>Custodian</MenuItem>
                <MenuItem value={"Employee"}>Employee</MenuItem>
                <MenuItem value={"HR"}>HR</MenuItem>
              </Select>
              {formErrors.role_applied ? <Typography color="error" variant="caption">{formErrors.role_applied}</Typography> : null}
            </FormControl>
            <TextField label="Application Date" value={currentDate} disabled fullWidth />
            <Button
              variant="outlined"
              component="label"
              sx={{ textTransform: "none", justifyContent: "flex-start" }}
            >
              {formData.resumeFile ? formData.resumeFile.name : "Upload Resume"}
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                hidden
                onChange={handleFileChange}
              />
            </Button>
            {formErrors.resumeFile ? <Typography color="error" variant="caption">{formErrors.resumeFile}</Typography> : null}
          </Box>
        </DialogContent>
        {/* Inline validation summary */}
        {(() => {
          const issues = [];
          if (!formData.firstname) issues.push('First name is required');
          if (!formData.lastname) issues.push('Last name is required');
          if (!formData.contact) issues.push('Contact is required');
          if (!formData.email) issues.push('Email is required');
          if (!formData.birthdate) issues.push('Birthdate is required');
          if (nameErrors.firstname) issues.push(nameErrors.firstname);
          if (!formData.noMiddleName && nameErrors.middlename) issues.push(nameErrors.middlename);
          if (nameErrors.lastname) issues.push(nameErrors.lastname);
          if (formErrors.email) issues.push(formErrors.email);
          if (formErrors.contact) issues.push(formErrors.contact);
          if (ageError) issues.push(ageError);
          // dedupe identical messages so duplicates (e.g. generic messages) do not appear multiple times
          const uniqueIssues = Array.from(new Set(issues));
          // only show the validation summary after the user attempted to submit
          if (!triedSubmit || uniqueIssues.length === 0) return null;
          return (
            <Box sx={{ px: 3, py: 1, bgcolor: 'rgba(255,235,238,0.7)', color: 'error.main', borderRadius: 1, mx: 2, mb: 1 }}>
              <Typography variant="body2">Please fix the following before submitting:</Typography>
              <ul style={{ margin: '6px 0 0 18px' }}>
                {uniqueIssues.map((it, idx) => (
                  <li key={idx}>
                    <Button
                      onClick={() => handleFocus(it)}
                      sx={{ textTransform: 'none', p: 0, minWidth: 0, color: 'error.main', justifyContent: 'flex-start' }}
                    >
                      <Typography component="span" variant="body2">{it}</Typography>
                    </Button>
                  </li>
                ))}
              </ul>
            </Box>
          );
        })()}

        <DialogActions>
          <Button onClick={handleCloseApply}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
